// @ts-nocheck
/**
 * OrderByKitContent — Custom grid, Kits cycled in right panel, Stores shown in grid
 *
 * Mirror of OrderBySkuContent but for Kit orders: cycle through Kits (instead of SKUs),
 * grid shows Stores for the selected Kit, distribute by store attributes.
 * Includes availability per-Kit (like Order By SKU).
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { REQUESTS } from 'utils/services/thetaretail/requests';
import { putAPIDataV3 } from 'utils/api';
import { removeItemFromLocalStorage } from 'utils/storage';
import { updateSelectedRows } from 'models/dynamicGrid/reducer';
import KitSelectionBox from './components/kitSelectionBox';
import DynamicFilterBox from 'pages/orderBySku/components/DynamicFilterBox';
import AgKitOrderGrid from './components/agKitOrderGrid';
import DistributeModal from 'pages/orderBySku/components/distributeModal';
import { Dot9loader } from 'molecules/dot9loader';
import * as obsSagaActions from './sagaActions';
import { fetchBookmarksList } from 'models/bookmarks/sagaActions';
import {
	SELECT_ROW_KEY,
	BULK_ITEMS_PER_PAGE,
	VALIDATION_ERROR_DISPLAY_MS,
	DEBOUNCE_SAVE_MS,
	DEFAULT_EXPORT_FILENAME,
} from './constants';
import { getObsKitColumns, getObsKitOrderData, getObsKitAvailability, getObsKitLoading } from './selector';
import './orderByKit.scss';

interface OrderByKitContentProps {
	tableId: number;
	orderStatusValue?: string;
}

const OrderByKitContent = forwardRef<any, OrderByKitContentProps>(({ tableId, orderStatusValue = '3' }, ref) => {
	const dispatch = useDispatch();
	const gridApiRef = useRef<any>(null);
	useMemo(() => {
		try {
			removeItemFromLocalStorage(tableId);
		} catch (e) {}
	}, [tableId]);

	/* ─── Direct API save buffer (bypasses saga — reliable even without Redux grid metadata) ─── */
	const bulkBuffer = useRef<Record<string, any>>({});
	const lastEditRow = useRef<any>(null);

	/** Send buffered edits directly to the UPDATE_ROW_DATA API */
	const flushSaveRef = useRef<(edits: Record<string, any>, refRow: any, orderKey: string) => Promise<void>>();
	flushSaveRef.current = async (edits: Record<string, any>, refRow: any, orderKey: string) => {
		const editValues = Object.values(edits);
		if (!refRow || !editValues.length) {
			console.warn('[OBS-Kit] flushSave skipped — no refRow or empty edits');
			return;
		}
		const keyValue = refRow[SELECT_ROW_KEY];
		if (keyValue === undefined || keyValue === null) {
			console.error(`[OBS-Kit] CRITICAL: ${SELECT_ROW_KEY} not found in row!`);
			return;
		}
		try {
			const orderId = refRow['OrderID'];
			const groupSearchParams = orderId != null ? [{ column: 'OrderID', value: orderId, type: 'number' }] : [];

			const body = {
				tableId,
				updatedValue: refRow[orderKey],
				rowIdentifier: orderKey,
				selectedRowKeyName: SELECT_ROW_KEY,
				selectedRowKeyValue: keyValue,
				BulkUpdateJSON: editValues,
				isMultiRowUpdate: true,
				pageNumber: 1,
				itemsPerPage: BULK_ITEMS_PER_PAGE,
				groupSearchParams,
				search: JSON.stringify({ filter: [] }),
				isAdvancedSearch: 1,
			};
			const response = await putAPIDataV3({
				service: REQUESTS.PUT.UPDATE_ROW_DATA.service,
				url: REQUESTS.PUT.UPDATE_ROW_DATA.url,
				body,
			});
			const respData = response?.data || response;

			/* Handle success: apply returned row data to allOrderDataRef */
			const updatedRows = respData?.updatedRows;
			const updateAvlQty = respData?.updateAvlQty;
			if (Array.isArray(updatedRows) && updatedRows.length > 0) {
				const returnedMap = new Map<string, any>();
				updatedRows.forEach((r: any) => {
					const id = r[SELECT_ROW_KEY] ?? r['OrderDetailID'];
					if (id != null) returnedMap.set(String(id), r);
				});

				if (Array.isArray(updateAvlQty)) {
					updateAvlQty.forEach((avl: any) => {
						const id = String(avl.RowIdentifier ?? avl[SELECT_ROW_KEY] ?? '');
						if (id && !returnedMap.has(id)) returnedMap.set(id, avl);
					});
				}

				const data = allOrderDataRef.current;
				const refreshNodes: any[] = [];
				const gridApi = gridApiRef.current;
				for (let i = 0; i < data.length; i++) {
					const rowId = String(data[i][SELECT_ROW_KEY] ?? data[i]['OrderDetailID'] ?? '');
					const returned = returnedMap.get(rowId);
					if (!returned) continue;
					Object.keys(returned).forEach((key) => {
						if (key === 'RowIdentifier') return;
						const val = returned[key];
						data[i][key] = val && typeof val === 'object' && val.value !== undefined ? val.value : val;
					});
					if (gridApi) {
						const node = gridApi.getRowNode(rowId);
						if (node) refreshNodes.push(node);
					}
				}

				if (gridApi && refreshNodes.length > 0) {
					gridApi.refreshCells({ rowNodes: refreshNodes, force: true });
				}
				bumpMetrics();
			}

			// Handle backend validation errors
			if (respData?.validationErrors?.length > 0) {
				const gridApi = gridApiRef.current;
				if (gridApi) {
					const errorMap = new Map();
					respData.validationErrors.forEach((ve: any) => {
						errorMap.set(String(ve.RowIdentifier), ve.ErrorMessage || 'Server validation failed');
					});
					const affectedNodes: any[] = [];
					gridApi.forEachNode((node: any) => {
						if (node.data && errorMap.has(String(node.data.OrderDetailID))) {
							const errorMsg = errorMap.get(String(node.data.OrderDetailID));
							node.data.OrderQty = 0;
							node.data._availError = errorMsg;
							affectedNodes.push(node);
							const masterRow = allOrderDataRef.current.find(
								(r) => String(r.OrderDetailID) === String(node.data.OrderDetailID)
							);
							if (masterRow) masterRow.OrderQty = 0;
						}
					});
					if (affectedNodes.length > 0) {
						gridApi.refreshCells({ rowNodes: affectedNodes, columns: ['OrderQty'], force: true });
						setTimeout(() => {
							const stillValid: any[] = [];
							affectedNodes.forEach((node) => {
								if (node.data?._availError) {
									delete node.data._availError;
									stillValid.push(node);
								}
							});
							if (stillValid.length > 0) {
								try {
									gridApi.refreshCells({ rowNodes: stillValid, columns: ['OrderQty'], force: true });
								} catch (e) {}
							}
						}, VALIDATION_ERROR_DISPLAY_MS);
					}
					bumpMetrics();
				}
			}
		} catch (e: any) {
			console.error('[OBS-Kit] Save FAILED:', e?.message || e, e?.response?.data || '');
		}
	};

	const debouncedSendBulk = useRef(
		_.debounce(() => {
			const row = lastEditRow.current;
			if (!row || !Object.keys(bulkBuffer.current).length) return;
			const orderKey = Object.keys(row).find((k) => /^orderqty$/i.test(k)) || 'OrderQty';
			flushSaveRef.current?.({ ...bulkBuffer.current }, row, orderKey);
			bulkBuffer.current = {};
		}, DEBOUNCE_SAVE_MS)
	).current;

	useEffect(() => {
		return () => {
			if (debouncedSendBulk?.cancel) debouncedSendBulk.cancel();
		};
	}, [debouncedSendBulk]);

	/* 1) Columns (via saga) */
	useEffect(() => {
		dispatch(obsSagaActions.fetchKitColumns({ tableId }));
	}, [tableId]);
	useEffect(() => {
		dispatch(fetchBookmarksList({ tableId: String(tableId) }));
	}, [tableId]);
	const { kitColumns, storeColumns, warehouseColumns, orderColumns } = useSelector(getObsKitColumns);

	const [kitIndex, setKitIndex] = useState(0);

	const [kitActiveKeys, setKitActiveKeys] = useState<string[]>([]);
	const [kitFilterValues, setKitFilterValues] = useState<Record<string, string>>({});
	const [storeActiveKeys, setStoreActiveKeys] = useState<string[]>([]);
	const [storeFilterValues, setStoreFilterValues] = useState<Record<string, string>>({});
	const [whActiveKeys, setWhActiveKeys] = useState<string[]>([]);
	const [whFilterValues, setWhFilterValues] = useState<Record<string, string>>({});
	const [orderActiveKeys, setOrderActiveKeys] = useState<string[]>([]);
	const [orderFilterValues, setOrderFilterValues] = useState<Record<string, string>>({});

	const [massMetric, setMassMetric] = useState('flat');
	const [massBaseQty, setMassBaseQty] = useState(0);
	const [distributeOpen, setDistributeOpen] = useState(false);

	/* 2) Fetch order data */
	const [refreshKey, setRefreshKey] = useState(0);
	useEffect(() => {
		dispatch(obsSagaActions.fetchKitOrderData({ tableId, orderStatusValue, refreshKey }));
	}, [tableId, orderStatusValue, refreshKey]);
	const obsOrderData = useSelector(getObsKitOrderData);
	const obsLoading = useSelector(getObsKitLoading);
	const dataLoading = obsLoading.data;
	const backgroundLoading = obsLoading.backgroundData;

	/* Mutable ref for in-place cell edits + version counters */
	const allOrderDataRef = useRef<any[]>([]);
	const [dataVersion, setDataVersion] = useState(0);
	const [metricsVersion, setMetricsVersion] = useState(0);
	const bumpData = useCallback(() => {
		setDataVersion((v) => v + 1);
		setMetricsVersion((v) => v + 1);
	}, []);
	const bumpMetrics = useCallback(() => setMetricsVersion((v) => v + 1), []);

	useEffect(() => {
		if (obsOrderData.rows.length > 0) {
			allOrderDataRef.current = obsOrderData.rows.map((r) => ({ ...r }));
			bumpData();
		}
	}, [obsOrderData.rows]);
	const allOrderData = allOrderDataRef.current;

	/* Expose refresh / resetView to parent */
	useImperativeHandle(
		ref,
		() => ({
			refresh: () => setRefreshKey((k) => k + 1),
			resetView: () => {
				if (gridApiRef.current) {
					gridApiRef.current.resetColumnState();
					gridApiRef.current.setFilterModel(null);
					gridApiRef.current.applyColumnState({ defaultState: { sort: null } });
				}
			},
			exportData: (fileName?: string) => {
				if (gridApiRef.current) {
					gridApiRef.current.exportDataAsCsv({
						fileName: fileName || DEFAULT_EXPORT_FILENAME,
						columnKeys: gridApiRef.current
							.getAllDisplayedColumns()
							?.filter((c) => c.getColId() !== 'ag-Grid-SelectionColumn')
							.map((c) => c.getColId()),
					});
				}
			},
		}),
		[]
	);

	/* Sync row selection to Redux */
	const handleSelectionChanged = useCallback(
		(selectedRows: any[]) => {
			const ids = selectedRows.map((r) => r.OrderDetailID).filter(Boolean);
			dispatch(
				updateSelectedRows({
					tableId,
					data: { selectedRowKeys: ids, isSelectAll: false, rowGroup: null },
				})
			);
		},
		[tableId, dispatch]
	);

	/* 3) Kit list derived from order data */
	const kitFieldName = useMemo(() => {
		if (!allOrderData.length) return 'SKU';
		const keys = Object.keys(allOrderData[0] || {});
		return (
			keys.find((k) => /^kitsku$/i.test(k)) ||
			keys.find((k) => /^sku$/i.test(k)) ||
			keys.find((k) => /^kitid$/i.test(k)) ||
			'SKU'
		);
	}, [dataVersion]);

	const kitDescFieldName = useMemo(() => {
		if (!allOrderData.length) return '';
		const keys = Object.keys(allOrderData[0] || {});
		return (
			keys.find((k) => /^kitdesc/i.test(k)) ||
			keys.find((k) => /kit.*desc/i.test(k)) ||
			keys.find((k) => /^skudesc/i.test(k)) ||
			keys.find((k) => /sku.*desc/i.test(k)) ||
			''
		);
	}, [dataVersion]);

	/* Store field name (for grid entity) */
	const storeFieldName = useMemo(() => {
		if (!allOrderData.length) return 'Store';
		return Object.keys(allOrderData[0] || {}).find((k) => /^store$/i.test(k)) || 'Store';
	}, [dataVersion]);

	/* 3a) Cross-filtering */
	const applyFilters = useCallback((data, filterVals) => {
		const active = Object.entries(filterVals).filter(([, v]) => v && v !== 'all');
		if (!active.length) return data;
		return data.filter((row) => active.every(([key, val]) => String(row[key] || '') === val));
	}, []);

	const allFilters = useMemo(
		() => ({ ...kitFilterValues, ...storeFilterValues, ...whFilterValues, ...orderFilterValues }),
		[kitFilterValues, storeFilterValues, whFilterValues, orderFilterValues]
	);
	const fullyFilteredData = useMemo(() => applyFilters(allOrderData, allFilters), [dataVersion, allFilters]);
	const dataForKitFilter = useMemo(
		() => applyFilters(allOrderData, { ...storeFilterValues, ...whFilterValues, ...orderFilterValues }),
		[dataVersion, storeFilterValues, whFilterValues, orderFilterValues]
	);
	const dataForStoreFilter = useMemo(
		() => applyFilters(allOrderData, { ...kitFilterValues, ...whFilterValues, ...orderFilterValues }),
		[dataVersion, kitFilterValues, whFilterValues, orderFilterValues]
	);
	const dataForWhFilter = useMemo(
		() => applyFilters(allOrderData, { ...kitFilterValues, ...storeFilterValues, ...orderFilterValues }),
		[dataVersion, kitFilterValues, storeFilterValues, orderFilterValues]
	);
	const dataForOrderFilter = useMemo(
		() => applyFilters(allOrderData, { ...kitFilterValues, ...storeFilterValues, ...whFilterValues }),
		[dataVersion, kitFilterValues, storeFilterValues, whFilterValues]
	);

	const mkToggle = (setter, valSetter) => (key) => {
		setter((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
		valSetter((prev) => {
			const n = { ...prev };
			delete n[key];
			return n;
		});
	};
	const handleKitToggle = useCallback(mkToggle(setKitActiveKeys, setKitFilterValues), []);
	const handleStoreToggle = useCallback(mkToggle(setStoreActiveKeys, setStoreFilterValues), []);
	const handleWhToggle = useCallback(mkToggle(setWhActiveKeys, setWhFilterValues), []);
	const handleOrderToggle = useCallback(mkToggle(setOrderActiveKeys, setOrderFilterValues), []);

	/* Kit sort state */
	const [kitSortField, setKitSortField] = useState<string>('');

	/* Build kit-level attribute column names for enrichment */
	const kitAttrFields = useMemo(() => {
		if (!kitColumns.length) return [] as string[];
		return kitColumns.map((c) => c.columnName);
	}, [kitColumns]);

	/* 3b) Kit list derived from cross-filtered data */
	const kitList = useMemo(() => {
		const source = dataForKitFilter.length ? dataForKitFilter : allOrderData;
		if (!source.length) return [];
		const seen = new Map();
		source.forEach((row) => {
			const kt = String(row[kitFieldName] || '');
			if (kt && !seen.has(kt)) {
				const kitObj: Record<string, any> = { [kitFieldName]: kt };
				if (kitDescFieldName) kitObj[kitDescFieldName] = row[kitDescFieldName] || '';
				kitAttrFields.forEach((f) => {
					if (row[f] !== undefined) kitObj[f] = row[f];
				});
				seen.set(kt, kitObj);
			}
		});
		const list = Array.from(seen.values());

		if (kitSortField) {
			list.sort((a, b) => {
				const va = String(a[kitSortField] || '').toLowerCase();
				const vb = String(b[kitSortField] || '').toLowerCase();
				const na = parseFloat(va),
					nb = parseFloat(vb);
				if (!isNaN(na) && !isNaN(nb)) return na - nb;
				return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
			});
		}

		return list;
	}, [dataVersion, kitFieldName, kitDescFieldName, kitAttrFields, dataForKitFilter, kitSortField]);

	const currentKit = kitList[kitIndex] || null;
	const kitId = currentKit ? String(currentKit[kitFieldName] || '') : '';

	const prevKitIdRef = useRef<string>('');
	useEffect(() => {
		if (!kitList.length) return;
		if (prevKitIdRef.current) {
			const newIdx = kitList.findIndex((s) => String(s[kitFieldName] || '') === prevKitIdRef.current);
			if (newIdx >= 0 && newIdx !== kitIndex) {
				setKitIndex(newIdx);
				return;
			}
		}
		if (kitIndex >= kitList.length) setKitIndex(0);
	}, [kitList]);

	useEffect(() => {
		prevKitIdRef.current = kitId;
	}, [kitId]);

	const handleKitNavigate = useCallback(
		(dir) => {
			const next = kitIndex + dir;
			if (next >= 0 && next < kitList.length) setKitIndex(next);
		},
		[kitIndex, kitList.length]
	);

	/* 4) Grid data for selected kit */
	const kitOrderData = useMemo(() => {
		if (!kitId || !allOrderData.length) return [];
		return fullyFilteredData.filter((row) => String(row[kitFieldName] || '') === kitId);
	}, [kitId, fullyFilteredData, kitFieldName]);

	/* All rows for selected kit (unfiltered) — used by DistributeModal for attribute discovery */
	const allKitOrderData = useMemo(() => {
		if (!kitId || !allOrderData.length) return [];
		return allOrderData.filter((row) => String(row[kitFieldName] || '') === kitId);
	}, [kitId, allOrderData, kitFieldName]);

	/* 5) Kit availability */
	const obsAvailability = useSelector(getObsKitAvailability);
	useEffect(() => {
		if (kitId) {
			dispatch(obsSagaActions.fetchKitAvailability({ kitId }));
		}
	}, [kitId]);

	const kitTotalOrderQty = useMemo(() => {
		if (!kitId || !allOrderDataRef.current.length) return 0;
		const keys = Object.keys(allOrderDataRef.current[0]);
		const orderKey = keys.find((k) => /^orderqty$/i.test(k)) || keys.find((k) => /order.*qty/i.test(k));
		let total = 0;
		allOrderDataRef.current.forEach((row) => {
			if (String(row[kitFieldName] || '') === kitId) {
				total += Number(row[orderKey] || 0);
			}
		});
		return total;
	}, [metricsVersion, kitId, kitFieldName]);

	const initialAvail = useMemo(() => {
		const raw = obsAvailability;
		if (!raw || typeof raw !== 'object') return 0;
		return Number(raw.Initial_Available_Qty || raw.AvailableQty || raw.Available || 0);
	}, [obsAvailability]);

	/* Detect the shelf-qty field name from order data (per-row, not a single global value) */
	const shelfQtyField = useMemo(() => {
		if (!allOrderDataRef.current.length) return 'QtyPerShelf';
		const keys = Object.keys(allOrderDataRef.current[0]);
		return keys.find((k) => /^qtypershelf$/i.test(k))
			|| keys.find((k) => /^ordermultiple$/i.test(k))
			|| 'QtyPerShelf';
	}, [dataVersion]);

	const effectiveAvail = initialAvail;
	const kitOrdTotal = kitTotalOrderQty;
	const kitRemain = effectiveAvail - kitOrdTotal;

	const availInfo = useMemo(
		() => ({
			initialAvail: effectiveAvail,
			currentAvail: effectiveAvail,
			orderedQty: kitOrdTotal,
			remainQty: kitRemain,
		}),
		[effectiveAvail, kitOrdTotal, kitRemain]
	);

	const applyDynamicFilter = useCallback(() => {}, []);

	/* ─── Mass update helper ─── */
	const massUpdateOrderQty = useCallback(
		(updateFn: (row: any) => number | null) => {
			const orderKey =
				Object.keys(allOrderDataRef.current[0] || {}).find((k) => /^orderqty$/i.test(k)) || 'OrderQty';
			const bulkEdits: { RowIdentifier: any; OrderQty: number }[] = [];
			let lastRow = null;

			allOrderDataRef.current = allOrderDataRef.current.map((row) => {
				if (String(row[kitFieldName]) !== kitId) return row;
				const newQty = updateFn(row);
				if (newQty === null) return row;
				const updated = { ...row, [orderKey]: newQty };
				bulkEdits.push({ RowIdentifier: row['OrderDetailID'], OrderQty: newQty });
				lastRow = updated;
				return updated;
			});

			if (bulkEdits.length > 0 && lastRow) {
				if (debouncedSendBulk?.cancel) debouncedSendBulk.cancel();
				bulkBuffer.current = {};
				const editsMap: Record<string, any> = {};
				bulkEdits.forEach((e) => {
					editsMap[e.RowIdentifier] = e;
				});
				flushSaveRef.current?.(editsMap, lastRow, orderKey);
			}

			bumpData();
		},
		[kitId, kitFieldName, bumpData, debouncedSendBulk]
	);

	const handleApplyMass = useCallback(() => {
		if (!kitId || !allOrderData.length) return;
		if (massMetric === 'flat') {
			let qty = massBaseQty > 0 ? massBaseQty : 0;
			if (qty === 0) {
				massUpdateOrderQty(() => 0);
				return;
			}
			massUpdateOrderQty(() => qty);
		}
	}, [massMetric, massBaseQty, kitId, dataVersion, massUpdateOrderQty]);

	const handleOrderQtyChange = useCallback(
		(rowIdx: number, storeId: string, newQty: number) => {
			if (!allOrderData.length) return;
			const orderKey = Object.keys(allOrderData[0]).find((k) => /^orderqty$/i.test(k)) || 'OrderQty';

			const data = allOrderDataRef.current;
			let editedRow = null;
			for (let i = 0; i < data.length; i++) {
				if (String(data[i][kitFieldName]) === kitId && String(data[i][storeFieldName]) === storeId) {
					data[i][orderKey] = newQty;
					editedRow = data[i];
					break;
				}
			}

			if (editedRow) {
				const rowId = editedRow['OrderDetailID'];
				const existing = bulkBuffer.current[rowId] || { RowIdentifier: rowId };
				bulkBuffer.current[rowId] = { ...existing, OrderQty: newQty };
				lastEditRow.current = editedRow;
				debouncedSendBulk();
			}

			bumpMetrics();
		},
		[dataVersion, kitId, kitFieldName, storeFieldName, bumpMetrics, debouncedSendBulk]
	);

	const isDistribute = massMetric === 'distribute';
	const canApply = massBaseQty >= 0;

	/* Groupable columns for grid — store + warehouse columns */
	const groupableColumns = useMemo(
		() => [...storeColumns, ...warehouseColumns].map((c) => ({ key: c.columnName, label: c.displayName })),
		[storeColumns, warehouseColumns]
	);

	/* Handle distribute modal apply — distributes by store attribute */
	const handleDistributeApply = useCallback(
		(storeQtyMap: Record<string, number>) => {
			if (!allOrderData.length) return;
			massUpdateOrderQty((row) => {
				const store = String(row[storeFieldName] || '');
				return store in storeQtyMap ? storeQtyMap[store] : null;
			});
		},
		[dataVersion, kitId, kitFieldName, storeFieldName, massUpdateOrderQty]
	);

	return (
		<div className="obs-bykit-wrapper">
			{dataLoading && <Dot9loader overlay message="Loading order data..." />}
			{backgroundLoading && <div className="obs-bg-loading-bar">Loading remaining records...</div>}
			<div className="obs-split-layout">
				<div className="obs-grid-area">
					<AgKitOrderGrid
						data={kitOrderData}
						onOrderQtyChange={handleOrderQtyChange}
						onSelectionChanged={handleSelectionChanged}
						groupableColumns={groupableColumns}
						kitAvailQty={effectiveAvail}
						shelfQtyField={shelfQtyField}
						kitLabel={kitId}
						kitDesc={currentKit?.[kitDescFieldName] || ''}
						kitOrdTotal={kitOrdTotal}
						kitRemain={kitRemain}
						gridApiRef={gridApiRef}
					/>
				</div>
				<div className="obs-right-panel">
					<KitSelectionBox
						kitList={kitList}
						kitIndex={kitIndex}
						onNavigate={handleKitNavigate}
						onSelectIndex={(idx) => setKitIndex(idx)}
						loading={dataLoading}
						availInfo={availInfo}
						sortOptions={kitColumns}
						sortField={kitSortField}
						onSortChange={setKitSortField}
					/>
					{kitColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter Kits"
							icon="📦"
							accentColor="#00695c"
							columns={kitColumns}
							gridData={dataForKitFilter}
							activeKeys={kitActiveKeys}
							onToggleKey={handleKitToggle}
							filterValues={kitFilterValues}
							onValueChange={(k, v) => setKitFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="Kits"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					{storeColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter Stores"
							icon="🏪"
							accentColor="#1565c0"
							columns={storeColumns}
							gridData={dataForStoreFilter}
							activeKeys={storeActiveKeys}
							onToggleKey={handleStoreToggle}
							filterValues={storeFilterValues}
							onValueChange={(k, v) => setStoreFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="Stores"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					{warehouseColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter Warehouse"
							icon="🏭"
							accentColor="#e65100"
							columns={warehouseColumns}
							gridData={dataForWhFilter}
							activeKeys={whActiveKeys}
							onToggleKey={handleWhToggle}
							filterValues={whFilterValues}
							onValueChange={(k, v) => setWhFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="Warehouses"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					{orderColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter Orders"
							icon="📋"
							accentColor="#f57c00"
							columns={orderColumns}
							gridData={dataForOrderFilter}
							activeKeys={orderActiveKeys}
							onToggleKey={handleOrderToggle}
							filterValues={orderFilterValues}
							onValueChange={(k, v) => setOrderFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="Orders"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					<div className="obs-box obs-box--mass">
						<div className="obs-box__title">⚡ Smart Mass Order</div>
						<div className="obs-mass__field">
							<div className="obs-label">Order Method</div>
							<select
								value={massMetric}
								onChange={(e) => {
									setMassMetric(e.target.value);
									if (e.target.value === 'distribute') setDistributeOpen(true);
								}}
								className="obs-box__select"
							>
								<option value="flat">Flat — Same qty to all stores</option>
								<option value="distribute">Distribute by Store Attribute</option>
							</select>
						</div>
						{isDistribute && (
							<div className="obs-mass__actions">
								<button
									onClick={() => setDistributeOpen(true)}
									className="obs-mass__apply-btn obs-mass__apply-btn--active"
								>
									Open Distribution Setup
								</button>
							</div>
						)}
						{!isDistribute && (
							<>
								<div className="obs-mass__inputs">
									<div>
										<div className="obs-label">Qty per Store</div>
										<input
											type="number"
											min={0}
											step={1}
											value={massBaseQty}
											onChange={(e) => setMassBaseQty(Number(e.target.value) || 0)}
											className="obs-box__input"
										/>
									</div>
								</div>
								<div className="obs-mass__actions">
									<button
										onClick={handleApplyMass}
										disabled={!canApply}
										className={`obs-mass__apply-btn ${canApply ? 'obs-mass__apply-btn--active' : ''}`}
									>
										Apply to All Stores
									</button>
									<button onClick={() => setMassBaseQty(0)} className="obs-mass__clear-btn">
										Clear
									</button>
								</div>
							</>
						)}
					</div>

					{/* Distribute by Store Attribute Modal */}
					<DistributeModal
						open={distributeOpen}
						onClose={() => setDistributeOpen(false)}
						skuOrderData={kitOrderData}
						allSkuData={allKitOrderData}
						skuFieldName={kitFieldName}
						skuId={kitId}
						initialAvail={effectiveAvail}
						shelfQtyField={shelfQtyField}
						onApply={handleDistributeApply}
						storeColumns={storeColumns}
					/>
				</div>
			</div>
		</div>
	);
});

OrderByKitContent.displayName = 'OrderByKitContent';
export default OrderByKitContent;
